import { useEffect, useState } from 'react';
import type {
  IPerson,
  IPersonListItem,
  IMergePreview,
} from '@skystone/shared';
import { personApi } from '@/services/person-api';
import { getErrorMessage } from '@/lib/utils';
import { Dialog, DialogFooter } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { KindBadge } from './KindBadge';

interface MergeWizardProps {
  open: boolean;
  winner: IPerson | null;
  onClose: () => void;
  onMerged: () => void;
}

type Step = 'pick' | 'preview' | 'confirm';

export function MergeWizard({ open, winner, onClose, onMerged }: Readonly<MergeWizardProps>) {
  const [step, setStep] = useState<Step>('pick');
  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState<IPersonListItem[]>([]);
  const [selected, setSelected] = useState<IPersonListItem | null>(null);
  const [searching, setSearching] = useState(false);
  const [preview, setPreview] = useState<IMergePreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setStep('pick');
      setQuery('');
      setCandidates([]);
      setSelected(null);
      setPreview(null);
      setError(null);
    }
  }, [open]);

  const runSearch = async (value: string): Promise<void> => {
    setSearching(true);
    setError(null);
    try {
      const response = await personApi.list({
        search: value || undefined,
        pageSize: 20,
      });
      const withoutSelf = winner
        ? response.items.filter((p) => p.id !== winner.id)
        : response.items;
      setCandidates(withoutSelf);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Search failed'));
    } finally {
      setSearching(false);
    }
  };

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    runSearch(query.trim());
  };

  const handleNextToPreview = async (): Promise<void> => {
    if (!winner || !selected) return;
    setLoadingPreview(true);
    setError(null);
    try {
      const response = await personApi.mergePreview(winner.id, { loserPersonId: selected.id });
      setPreview(response);
      setStep('preview');
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to build merge preview'));
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleConfirmMerge = async (): Promise<void> => {
    if (!winner || !preview) return;
    setSubmitting(true);
    setError(null);
    try {
      await personApi.merge(winner.id, { loserPersonId: preview.loser.id });
      onMerged();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Merge failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const laterStepTitle = step === 'preview' ? 'Review merge' : 'Confirm merge';
  const title = step === 'pick' ? 'Merge with another Person' : laterStepTitle;

  return (
    <Dialog open={open} onClose={onClose} title={title} maxWidth="max-w-2xl">
      {winner && (
        <div className="mb-3 rounded-md border bg-muted/40 p-3 text-sm">
          <span className="text-muted-foreground">Winner (kept):</span>{' '}
          <span className="font-medium">{winner.displayName}</span>
          <span className="ml-2 text-xs text-muted-foreground">{winner.primaryEmail ?? '—'}</span>
        </div>
      )}

      {step === 'pick' && (
        <PickStep
          query={query}
          setQuery={setQuery}
          candidates={candidates}
          selected={selected}
          setSelected={setSelected}
          searching={searching}
          onSubmit={handleSearchSubmit}
        />
      )}

      {step === 'preview' && preview && (
        <MergePreviewBody preview={preview} />
      )}

      {error && <p className="mt-3 text-sm text-error-700">{error}</p>}

      <DialogFooter>
        {step === 'pick' && (
          <>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={handleNextToPreview} isLoading={loadingPreview} disabled={!selected || loadingPreview}>
              Next
            </Button>
          </>
        )}
        {step === 'preview' && (
          <>
            <Button variant="ghost" onClick={() => setStep('pick')} disabled={submitting}>Back</Button>
            <Button onClick={handleConfirmMerge} isLoading={submitting} disabled={submitting}>
              Merge now
            </Button>
          </>
        )}
      </DialogFooter>
    </Dialog>
  );
}

interface PickStepProps {
  query: string;
  setQuery: (v: string) => void;
  candidates: IPersonListItem[];
  selected: IPersonListItem | null;
  setSelected: (p: IPersonListItem) => void;
  searching: boolean;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
}

function PickStep({
  query,
  setQuery,
  candidates,
  selected,
  setSelected,
  searching,
  onSubmit,
}: Readonly<PickStepProps>) {
  return (
    <div>
      <form onSubmit={onSubmit} className="mb-3 flex gap-2">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search Person to merge in (will be tombstoned)…"
          className="flex-1 rounded-md border bg-background px-3 py-2 text-sm"
        />
        <Button type="submit" size="sm" isLoading={searching}>Search</Button>
      </form>
      <div className="max-h-64 overflow-auto rounded-md border">
        {candidates.length === 0 && !searching && (
          <p className="p-3 text-sm text-muted-foreground">Search to pick the loser Person.</p>
        )}
        <ul className="divide-y">
          {candidates.map((person) => (
            <li key={person.id}>
              <button
                type="button"
                onClick={() => setSelected(person)}
                className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-muted/50 ${
                  selected?.id === person.id ? 'bg-muted' : ''
                }`}
              >
                <div className="min-w-0">
                  <div className="truncate font-medium">{person.displayName}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {person.primaryEmail ?? '—'}
                  </div>
                </div>
                <KindBadge kind={person.kind} />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function MergePreviewBody({ preview }: Readonly<{ preview: IMergePreview }>) {
  const { loser, fieldDiffs, linksToMove, duplicateLinksToDelete } = preview;
  return (
    <div className="space-y-3 text-sm">
      <div className="rounded-md border bg-muted/40 p-3">
        <span className="text-muted-foreground">Loser (tombstoned):</span>{' '}
        <span className="font-medium">{loser.displayName}</span>
        <span className="ml-2 text-xs text-muted-foreground">{loser.primaryEmail ?? '—'}</span>
      </div>

      <div className="rounded-md border border-warning-200 bg-warning-50 p-3 text-warning-700">
        <strong>This cannot be easily undone.</strong> The loser Person will be tombstoned{' '}
        (<code>is_active=false</code>, <code>merged_into_person_id={'<winner>'}</code>). Links will move
        to the winner. The loser row stays in the DB for audit.
      </div>

      {fieldDiffs.length > 0 && (
        <div>
          <h4 className="mb-1 text-xs font-semibold uppercase text-muted-foreground">
            Field differences (winner's values are kept)
          </h4>
          <ul className="divide-y rounded-md border">
            {fieldDiffs.map((d) => (
              <li key={d.field} className="flex justify-between gap-3 px-3 py-2">
                <span className="font-medium">{d.field}</span>
                <span className="text-xs">
                  <span className="text-muted-foreground">winner:</span>{' '}
                  {d.winnerValue ?? '—'} <span className="mx-1 text-muted-foreground">|</span>{' '}
                  <span className="text-muted-foreground">loser:</span> {d.loserValue ?? '—'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <h4 className="mb-1 text-xs font-semibold uppercase text-muted-foreground">
          Links that will move ({linksToMove.length})
        </h4>
        {linksToMove.length === 0 && (
          <p className="text-xs text-muted-foreground">No links will move.</p>
        )}
        <ul className="text-xs">
          {linksToMove.map((l) => (
            <li key={l.id}>
              {l.source} · <code>{l.sourceAccountId}</code> (match: {l.matchSource})
            </li>
          ))}
        </ul>
      </div>

      {duplicateLinksToDelete.length > 0 && (
        <div>
          <h4 className="mb-1 text-xs font-semibold uppercase text-muted-foreground">
            Duplicates that will be deleted from loser ({duplicateLinksToDelete.length})
          </h4>
          <ul className="text-xs">
            {duplicateLinksToDelete.map((l) => (
              <li key={l.id}>
                {l.source} · <code>{l.sourceAccountId}</code>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
